import { TachographConnectivity, TachographStatus, TachographTab } from "../models/Tachograph";
import { DAY_MS } from "./tachographDates";

/** A download is flagged a week before it is due. */
const WARNING_WINDOW_MS = 7 * DAY_MS;

/** A tachograph that has not reported for this long counts as unreliable. */
const DEGRADED_SIGNAL_AGE_MS = 2 * DAY_MS;

/** Beyond this the tachograph counts as unreachable. */
const OFFLINE_SIGNAL_AGE_MS = 14 * DAY_MS;

export function getStatusColor(status: TachographStatus | TachographConnectivity): string {
  if (status === "ok" || status === "online") return "success.main";
  if (status === "warning" || status === "degraded") return "warning.main";

  return "error.main";
}

function statusRank(status: TachographStatus): number {
  if (status === "overdue") return 3;
  if (status === "warning") return 2;

  return 1;
}

/** The worst of the single statuses wins. */
export function combineOverallStatus(...statuses: TachographStatus[]): TachographStatus {
  return statuses.reduce<TachographStatus>(
    (worst, candidate) => (statusRank(candidate) > statusRank(worst) ? candidate : worst),
    "ok",
  );
}

/** Due date is the reference date plus the interval; without a reference date nothing is known. */
export function deriveStatusFromReferenceDate(
  referenceDate: Date | null,
  intervalDays: number,
  now: Date,
): TachographStatus {
  if (!referenceDate) return "warning";

  const remainingMs = referenceDate.getTime() + intervalDays * DAY_MS - now.getTime();
  if (remainingMs < 0) return "overdue";
  if (remainingMs <= WARNING_WINDOW_MS) return "warning";

  return "ok";
}

export function deriveConnectivityStatus(lastSignalDate: Date | null, now: Date): TachographConnectivity {
  if (!lastSignalDate) return "degraded";

  const ageMs = now.getTime() - lastSignalDate.getTime();
  if (ageMs <= DEGRADED_SIGNAL_AGE_MS) return "online";
  if (ageMs <= OFFLINE_SIGNAL_AGE_MS) return "degraded";

  return "offline";
}

/** The tab a row shows up in: the first problem found decides. */
export function getRowCategory(
  downloadStatus: TachographStatus,
  inspectionStatus: TachographStatus,
  companyCardStatus: TachographStatus,
  connectorStatus: TachographConnectivity,
): TachographTab {
  if (downloadStatus !== "ok") return "downloads";
  if (inspectionStatus !== "ok") return "inspections";
  if (companyCardStatus !== "ok") return "companyCards";
  if (connectorStatus !== "online") return "connectors";

  return "status";
}
